import { useState, useCallback, useEffect } from 'react';
import { PlaybookTransformItem, PlaybookMapping, PlaybookEnrichment } from '@/types/playbook';
import { UnifiedSchema } from '@/types/schema';
import { 
  getPlaybookAIMappingSuggestionsBatch, 
  retryFailedPlaybookMappings, 
  PlaybookAIMappingResult 
} from '@/services/playbookAIMappingService';
import { MappingSummaryData } from '@/components/dialogs/MappingSummaryDialog';
import { findParameterDataTypeInSchema } from '@/utils/schemaUtils';
import { EditorSelection } from '@/components/playbooks/PlaybookEditorTab';

interface UsePlaybookAIMappingProps {
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  transforms: PlaybookTransformItem[];
  onAddTransforms: (transforms: PlaybookTransformItem[]) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  onBatchUpdateTransforms?: (batchUpdates: { [transformId: string]: Partial<PlaybookTransformItem> }) => void;
  selection: EditorSelection;
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  baselineSchema?: 'input' | 'output';
  isPropertyPairApplicableToAllEvents: (inputPath: string | undefined, outputPath: string | undefined) => boolean;
}

interface AIProgressDetail {
  current: number;
  total: number;
  message?: string;
  suggestions?: PlaybookAIMappingResult[];
}

interface AIMappingStats {
  total: number;
  valid: number;
  invalid: number;
  matched: number;
  unmatched: number;
}

export const usePlaybookAIMapping = ({
  inputSchema,
  outputSchema,
  transforms,
  onAddTransforms,
  onUpdateTransform,
  onBatchUpdateTransforms,
  selection,
  allUiTransforms,
  allowUndefinedTransforms,
  baselineSchema,
  isPropertyPairApplicableToAllEvents,
}: UsePlaybookAIMappingProps) => {
  // AI Mapping state
  const [showAIMappingProgressDialog, setShowAIMappingProgressDialog] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiProgressDetail, setAiProgressDetail] = useState<AIProgressDetail>();
  const [aiMappingError, setAiMappingError] = useState<string>();
  const [aiMappingComplete, setAiMappingComplete] = useState(false);
  const [aiMappingStats, setAiMappingStats] = useState<AIMappingStats>();
  const [aiMappingPartialSuccess, setAiMappingPartialSuccess] = useState(false);
  const [aiMappingAbortController, setAiMappingAbortController] = useState<AbortController | null>(null);
  
  // AI Mapping summary dialog state
  const [showAIMappingSummary, setShowAIMappingSummary] = useState(false);
  const [aiMappingSummaryData, setAiMappingSummaryData] = useState<MappingSummaryData | null>(null);
  const [pendingAITransforms, setPendingAITransforms] = useState<PlaybookTransformItem[]>([]);

  // Helper function to determine transform scope
  const determineTransformScope = useCallback((inputPath?: string, outputPath?: string) => {
    const isGlobalCandidate = isPropertyPairApplicableToAllEvents(inputPath, outputPath);
    
    if (allowUndefinedTransforms && isGlobalCandidate) {
      return {
        appliesToScope: 'global' as const,
        eventIds: undefined
      };
    } else {
      return {
        appliesToScope: 'eventSpecific' as const,
        eventIds: selection.type === 'event' && selection.eventId ? [selection.eventId] : undefined
      };
    }
  }, [allowUndefinedTransforms, isPropertyPairApplicableToAllEvents, selection]);

  // Helper function to create transform with proper typing
  const createTransform = useCallback((
    inputPath: string,
    outputPath: string,
    mappingType: 'direct' | 'ai' | 'no-match',
    isEnrichment = false,
    certaintyScore?: number
  ): PlaybookTransformItem => {
    const { appliesToScope, eventIds } = determineTransformScope(inputPath, outputPath);
    const inputType = inputPath && inputSchema ? findParameterDataTypeInSchema(inputSchema, inputPath) : undefined;
    const outputType = outputPath && outputSchema ? findParameterDataTypeInSchema(outputSchema, outputPath) : undefined;

    const uniqueId = `transform_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    if (isEnrichment) {
      return {
        id: uniqueId,
        type: 'enrichment',
        content: {
          type: 'DirectValue',
          outputKey: outputPath,
          value: '',
        } as PlaybookEnrichment,
        appliesToScope,
        eventIds,
        mappingType,
        inputPath: '',
        outputPath,
        inputType: undefined,
        outputType,
        __meta: certaintyScore ? { certaintyScore } : undefined
      };
    } else {
      return {
        id: uniqueId,
        type: 'mapping',
        content: {
          inputKey: inputPath,
          outputKey: outputPath,
          transforms: outputPath ? [{ type: 'DirectAssign' }] : [],
        } as PlaybookMapping,
        appliesToScope,
        eventIds,
        mappingType,
        inputPath,
        outputPath,
        inputType,
        outputType,
        __meta: certaintyScore ? { certaintyScore } : undefined
      };
    }
  }, [inputSchema, outputSchema, determineTransformScope]);

  // Helper function to check for existing shared transforms
  const findExistingSharedTransform = useCallback((
    inputPath: string,
    outputPath: string,
    isEnrichment = false
  ) => {
    if (selection.type !== 'event' || !selection.eventId) return null;

    return allUiTransforms.find(t => {
      if (isEnrichment) {
        return t.type === 'enrichment' &&
               (t.content as PlaybookEnrichment)?.outputKey === outputPath &&
               t.appliesToScope === 'eventSpecific' &&
               t.eventIds && t.eventIds.length > 0 &&
               !t.eventIds.includes(selection.eventId!);
      } else {
        return t.type === 'mapping' &&
               (t.content as PlaybookMapping)?.inputKey === inputPath &&
               (t.content as PlaybookMapping)?.outputKey === outputPath &&
               t.appliesToScope === 'eventSpecific' &&
               t.eventIds && t.eventIds.length > 0 &&
               !t.eventIds.includes(selection.eventId!);
      }
    });
  }, [allUiTransforms, selection]);

  // Helper function to update existing shared transform
  const updateExistingSharedTransform = useCallback((
    existingTransform: PlaybookTransformItem,
    logMessage: string
  ) => {
    if (selection.eventId) {
      onUpdateTransform(existingTransform.id, {
        eventIds: [...(existingTransform.eventIds || []), selection.eventId],
      });
      console.log(logMessage);
    }
  }, [onUpdateTransform, selection.eventId]);

  // Helper function to get properties needing AI matching
  const getPropertiesNeedingAI = useCallback(() => {
    if (!inputSchema || !outputSchema) return [];

    const effectiveBaselineSchema = baselineSchema || 'input'; // Use the effective baseline

    let sourceSchema: UnifiedSchema;
    let isInputBaseline = false;

    if (effectiveBaselineSchema === 'input') {
      sourceSchema = inputSchema;
      isInputBaseline = true;
    } else { // effectiveBaselineSchema === 'output'
      sourceSchema = outputSchema;
      isInputBaseline = false;
    }

    const processedSourcePaths = new Set<string>();
    let propertiesNeedingAI: { id: string; name: string; path: string; type: string; transformId?: string; }[] = [];

    // Determine existing mapped paths based on the baseline
    const existingMappedPaths = transforms
      .filter(t => t.type === 'mapping' || t.type === 'enrichment')
      .map(t => {
        if (isInputBaseline) {
          return (t.content as PlaybookMapping)?.inputKey;
        } else { // Output baseline
          return (t.content as PlaybookMapping)?.outputKey || (t.content as PlaybookEnrichment)?.outputKey;
        }
      })
      .filter(Boolean) as string[];

    // Find unmapped properties in the source schema
    const unmappedSourceProperties = sourceSchema.parameters
      .filter(param => !existingMappedPaths.includes(param.path))
      .map(param => {
        processedSourcePaths.add(param.path);
        return {
          id: param.id,
          name: param.friendlyName || param.codeName,
          path: param.path,
          type: param.dataTypes[0] || 'string'
        };
      });

    // Find partial mappings (source property exists, but target is missing/empty)
    const partialMappings = transforms
      .filter(t => {
        if (isInputBaseline) {
          return t.type === 'mapping' &&
                 t.inputPath && t.inputPath.trim() !== '' &&
                 (!t.outputPath || t.outputPath.trim() === '') &&
                 !processedSourcePaths.has(t.inputPath);
        } else { // Output baseline
          const outputKey = (t.content as PlaybookMapping)?.outputKey || (t.content as PlaybookEnrichment)?.outputKey;
          return (t.type === 'mapping' || t.type === 'enrichment') &&
                 outputKey && outputKey.trim() !== '' &&
                 (t.type === 'mapping' ? (!t.inputPath || t.inputPath.trim() === '') : false) && // For mappings, check if input is missing
                 !processedSourcePaths.has(outputKey);
        }
      })
      .map(t => {
        let paramPath: string;
        let param: any; // UnifiedSchemaParameter
        if (isInputBaseline) {
          paramPath = t.inputPath || '';
          param = inputSchema.parameters.find(p => p.path === paramPath);
        } else { // Output baseline
          paramPath = (t.content as PlaybookMapping)?.outputKey || (t.content as PlaybookEnrichment)?.outputKey || '';
          param = outputSchema.parameters.find(p => p.path === paramPath);
        }
        return {
          id: param?.id || paramPath || `partial_${t.id}`,
          name: param?.friendlyName || param?.codeName || paramPath.split('.').pop() || 'Unknown',
          path: paramPath,
          type: (param?.dataTypes[0] || 'string') as string,
          transformId: t.id
        };
      });

    propertiesNeedingAI = [...unmappedSourceProperties, ...partialMappings];

    // Filter out properties that already have AI suggestions with sufficient certainty
    return propertiesNeedingAI.filter(property => {
      const existingTransform = transforms.find(t => {
        if (isInputBaseline) {
          return t.inputPath === property.path && t.mappingType === 'ai' && t.__meta?.certaintyScore !== undefined;
        } else { // Output baseline
          return ((t.content as PlaybookMapping)?.outputKey === property.path || (t.content as PlaybookEnrichment)?.outputKey === property.path) &&
                 t.mappingType === 'ai' && t.__meta?.certaintyScore !== undefined;
        }
      });
      
      // Always include partial mappings that need completion
      if (property.transformId) {
        return true;
      }
      
      // Only re-process if no existing AI mapping or certainty score is low
      return !existingTransform || 
             (existingTransform.__meta?.certaintyScore !== undefined && 
              existingTransform.__meta.certaintyScore < 0.5);
    });
  }, [inputSchema, outputSchema, transforms, baselineSchema]);

  // Apply AI suggestions in real-time
  const applyAISuggestions = useCallback((suggestions: PlaybookAIMappingResult[]) => {
    console.log(`🔄 Applying ${suggestions.length} AI suggestions:`, suggestions);
    
    const newTransformsToAdd: PlaybookTransformItem[] = [];
    const transformUpdates: { [transformId: string]: Partial<PlaybookTransformItem> } = {};
    const effectiveBaselineSchema = baselineSchema || 'input'; // Use the effective baseline

    // Process all suggestions first to collect updates
    suggestions.forEach((suggestion, index) => {
      console.log(`Processing suggestion ${index + 1}/${suggestions.length}:`, {
        inputPath: suggestion.inputPath,
        normalizedPath: suggestion.normalizedPath,
        certaintyScore: suggestion.certaintyScore,
        matchStatus: suggestion.matchStatus
      });

      // Only process suggestions with high certainty or if no existing mapping exists
      if (suggestion.certaintyScore > 0.6 || suggestion.matchStatus === 'matched') {
        // Determine input and output paths based on baseline and suggestion
        let inputPath: string | undefined;
        let outputPath: string | undefined;
        let isEnrichment = false;

        if (effectiveBaselineSchema === 'input') {
          inputPath = suggestion.inputPath;
          outputPath = suggestion.normalizedPath;
          isEnrichment = suggestion.matchStatus === 'no-match'; // If input baseline, no-match means no output mapping
        } else { // effectiveBaselineSchema === 'output'
          inputPath = suggestion.normalizedPath; // Matched input path
          outputPath = suggestion.inputPath; // Original output path from the suggestion
          isEnrichment = suggestion.matchStatus === 'no-match'; // If output baseline, no-match means enrichment
        }

        console.log(`Determined paths for suggestion ${index + 1}: inputPath="${inputPath}", outputPath="${outputPath}", isEnrichment=${isEnrichment}`);

        const existingTransform = transforms.find(t => {
          if (effectiveBaselineSchema === 'input') {
            return t.inputPath === inputPath;
          } else { // Output baseline
            return (t.content as PlaybookMapping)?.outputKey === outputPath || (t.content as PlaybookEnrichment)?.outputKey === outputPath;
          }
        });
        
        if (existingTransform) {
          console.log(`Found existing transform to update: ${existingTransform.id}`);
          // Collect update for existing transform
          const inputType = inputPath && inputSchema ? findParameterDataTypeInSchema(inputSchema, inputPath) : undefined;
          const outputType = outputPath && outputSchema ? findParameterDataTypeInSchema(outputSchema, outputPath) : undefined;
          
          transformUpdates[existingTransform.id] = {
            outputPath: outputPath, // Update output path
            inputPath: inputPath, // Update input path (for output baseline case)
            mappingType: 'ai',
            inputType,
            outputType,
            __meta: { certaintyScore: suggestion.certaintyScore },
            content: {
              ...(existingTransform.content as PlaybookMapping),
              inputKey: inputPath, // Ensure inputKey is updated for mappings
              outputKey: outputPath, // Ensure outputKey is updated for mappings/enrichments
              transforms: [{ type: 'DirectAssign' }], // Ensure transforms array is set for mappings
            }
          };
        } else {
          console.log(`No existing transform found, checking for shared transform`);
          // Create new transform
          // Check for existing shared transform
          const existingSharedTransform = findExistingSharedTransform(inputPath, outputPath, isEnrichment);
          
          if (existingSharedTransform) {
            console.log(`Found existing shared transform to update: ${existingSharedTransform.id}`);
            updateExistingSharedTransform(
              existingSharedTransform,
              `Updated existing shared AI ${isEnrichment ? 'enrichment' : 'mapping'} for '${inputPath || 'none'}' -> '${outputPath}' to include event ${selection.eventId}`
            );
          } else {
            console.log(`Creating new transform for suggestion ${index + 1}`);
            const newTransform = createTransform(
              inputPath || '', // Ensure string
              outputPath || '', // Ensure string
              'ai',
              isEnrichment,
              suggestion.certaintyScore
            );
            
            newTransformsToAdd.push(newTransform);
          }
        }
      } else {
        console.log(`Skipping suggestion ${index + 1} due to low certainty score (${suggestion.certaintyScore}) and no match status`);
      }
    });
    
    console.log(`Collected ${Object.keys(transformUpdates).length} transform updates and ${newTransformsToAdd.length} new transforms`);
    
    console.log(`✅ Finished collecting AI suggestions`);
    
    return { newTransformsToAdd, transformUpdates };
  }, [
    transforms, 
    inputSchema, 
    outputSchema, 
    baselineSchema, 
    findExistingSharedTransform, 
    updateExistingSharedTransform, 
    createTransform, 
    selection.eventId
  ]);

  // AI Match functionality
  const handleAIMatch = useCallback(() => {
    console.log('usePlaybookAIMapping: handleAIMatch called');
    console.log('usePlaybookAIMapping: inputSchema', inputSchema);
    console.log('usePlaybookAIMapping: outputSchema', outputSchema);
    if (!inputSchema || !outputSchema) {
      console.warn('Cannot use AI matching: input or output schema is missing');
      return;
    }

    const effectiveBaselineSchema = baselineSchema || 'input'; // Add this line

    const propertiesNeedingAI = getPropertiesNeedingAI();

    if (propertiesNeedingAI.length === 0) {
      console.log('No properties need AI matching - all are already completely mapped or have been attempted with sufficient certainty');
      return;
    }

    console.log(`Starting AI matching for ${propertiesNeedingAI.length} properties (including ${propertiesNeedingAI.filter(p => p.transformId).length} partial mappings) with baseline schema: ${baselineSchema || 'none'}`);

    // Prepare target properties for matching based on baseline schema
    const targetProperties = (effectiveBaselineSchema === 'input' ? outputSchema : inputSchema).parameters.map(param => ({
      name: param.friendlyName || param.codeName,
      path: param.path,
      type: param.dataTypes[0] || 'string'
    }));

    // Reset AI mapping state
    setAiProgress(0);
    setAiProgressDetail(undefined);
    setAiMappingError(undefined);
    setAiMappingComplete(false);
    setAiMappingStats(undefined);
    setAiMappingPartialSuccess(false);
    setShowAIMappingProgressDialog(true);

    // Create abort controller for cancellation
    const abortController = new AbortController();
    setAiMappingAbortController(abortController);

    // Start AI mapping
    getPlaybookAIMappingSuggestionsBatch(
      propertiesNeedingAI,
      targetProperties,
      (progress, details) => {
        setAiProgress(progress);
        if (details) {
          setAiProgressDetail(details);
          
        }
      },
      abortController.signal
    )
      .then(result => {
        setAiMappingComplete(true);
        setAiMappingStats(result.stats);
        setAiMappingPartialSuccess(result.stats.matched < result.stats.total);

        const successCount = result.stats?.matched || 0;
        const totalCount = result.stats?.total || 0;

        if (result.suggestions && result.suggestions.length > 0) {
          const { newTransformsToAdd, transformUpdates } = applyAISuggestions(result.suggestions);
          
          console.log(`Applying ${Object.keys(transformUpdates).length} transform updates:`, transformUpdates);
          
          // Apply all transform updates in a single batch
          if (Object.keys(transformUpdates).length > 0) {
            // Prepare final updates with proper typing
            const finalTransformUpdates: { [transformId: string]: Partial<PlaybookTransformItem> } = {};
            
            Object.entries(transformUpdates).forEach(([transformId, updates]) => {
              console.log(`Preparing batch update for transform ${transformId}:`, updates);
              finalTransformUpdates[transformId] = {
                ...updates,
                type: 'mapping',
                content: {
                  ...(updates.content || {}),
                  transforms: [{ type: 'DirectAssign' }]
                }
              };
            });
            
            // Use batch update if available, otherwise fall back to individual updates
            if (onBatchUpdateTransforms) {
              console.log(`🔄 Using batch update for ${Object.keys(finalTransformUpdates).length} transforms`);
              onBatchUpdateTransforms(finalTransformUpdates);
            } else {
              console.log(`🔄 Falling back to individual updates for ${Object.keys(finalTransformUpdates).length} transforms`);
              Object.entries(finalTransformUpdates).forEach(([transformId, updates]) => {
                onUpdateTransform(transformId, updates);
              });
            }
          }
          
          if (newTransformsToAdd.length > 0) {
            console.log(`Adding ${newTransformsToAdd.length} new transforms`);
            onAddTransforms(newTransformsToAdd);
          }
        }

        console.log(`AI mapping completed: ${successCount} of ${totalCount} properties mapped`);
      })
      .catch(error => {
        console.error('AI mapping error:', error);
        const errorMessage = error.message || 'An error occurred during AI mapping';
        setAiMappingError(errorMessage);
        setAiMappingComplete(true);
        
        // Check if we have partial results
        const hasPartialResults = aiMappingStats && aiMappingStats.matched > 0;
        if (!hasPartialResults) {
          console.error(`AI mapping failed: ${errorMessage}`);
        }
      })
      .finally(() => {
        setAiMappingAbortController(null);
      });
  }, [
    inputSchema,
    outputSchema,
    getPropertiesNeedingAI,
    baselineSchema,
    applyAISuggestions, // Keep this dependency as applyAISuggestions is called
    aiMappingStats
  ]);

  // Handle retry for failed AI mappings
  const handleRetryAIMapping = useCallback(() => {
    if (!inputSchema || !outputSchema || !aiProgressDetail?.suggestions) {
      console.warn('Cannot retry AI mapping: missing required data');
      return;
    }

    const effectiveBaselineSchema = baselineSchema || 'input';

    // Get failed mappings that haven't been successfully mapped yet
    const failedSuggestions = aiProgressDetail.suggestions.filter(s => 
      s.matchStatus === 'no-match' || 
      (s.certaintyScore !== undefined && s.certaintyScore < 0.5)
    );
    
    if (failedSuggestions.length === 0) {
      console.log('No failed mappings to retry');
      return;
    }

    console.log(`Retrying AI mapping for ${failedSuggestions.length} failed properties with baseline schema: ${effectiveBaselineSchema}`);

    // Find the original properties for the failed suggestions based on baseline
    const failedProperties = failedSuggestions.map(suggestion => {
      let param: any;
      let path: string;
      if (effectiveBaselineSchema === 'input') {
        path = suggestion.inputPath;
        param = inputSchema.parameters.find(p => p.path === path);
      } else { // Output baseline
        path = suggestion.inputPath; // In reverse mapping, inputPath of suggestion is the output path
        param = outputSchema.parameters.find(p => p.path === path);
      }
      return {
        id: param?.id || path,
        name: param?.friendlyName || param?.codeName || path.split('.').pop() || '',
        path: path,
        type: param?.dataTypes[0] || 'string'
      };
    });

    // Prepare target properties for matching based on baseline schema
    const targetProperties = (effectiveBaselineSchema === 'input' ? outputSchema : inputSchema).parameters.map(param => ({
      name: param.friendlyName || param.codeName,
      path: param.path,
      type: param.dataTypes[0] || 'string'
    }));

    // Reset AI mapping state for retry
    setAiProgress(0);
    setAiMappingError(undefined);
    setAiMappingComplete(false);
    setAiMappingStats(undefined);
    setAiMappingPartialSuccess(false);

    // Create abort controller for cancellation
    const abortController = new AbortController();
    setAiMappingAbortController(abortController);

    // Start retry - note that retryFailedPlaybookMappings actually calls getPlaybookAIMappingSuggestionsBatch
    // which does include suggestions in the progress callback
    retryFailedPlaybookMappings(
      failedProperties,
      targetProperties,
      (progress, details) => {
        setAiProgress(progress);
        if (details) {
          // Update progress detail with new information
          setAiProgressDetail({
            current: details.current,
            total: details.total,
            message: details.message,
            suggestions: (details as any).suggestions || [] // Cast to access suggestions property
          });
        }
      },
      abortController.signal
    )
      .then(result => {
        setAiMappingComplete(true);
        setAiMappingStats(result.stats);
        setAiMappingPartialSuccess(result.stats.matched < result.stats.total);

        // Apply all suggestions from the final result
        if (result.suggestions && result.suggestions.length > 0) {
          const { newTransformsToAdd, transformUpdates } = applyAISuggestions(result.suggestions);
          
          // Apply all transform updates at once
          console.log(`Applying ${Object.keys(transformUpdates).length} transform updates for retry:`, transformUpdates);
          
          // Process each transform update individually
          for (const [transformId, updates] of Object.entries(transformUpdates)) {
            console.log(`Applying retry update for transform ${transformId}:`, updates);
            // Ensure we're setting the transform type to 'mapping' and including the transforms array
            const finalUpdates = {
              ...updates,
              type: 'mapping',
              content: {
                ...(updates.content || {}),
                transforms: [{ type: 'DirectAssign' }]
              }
            };
            onUpdateTransform(transformId, finalUpdates);
          }
          
          if (newTransformsToAdd.length > 0) {
            console.log(`Adding ${newTransformsToAdd.length} new transforms from retry`);
            onAddTransforms(newTransformsToAdd);
          }
        }

        const successCount = result.stats?.matched || 0;
        const totalCount = result.stats?.total || 0;
        console.log(`AI retry completed: ${successCount} of ${totalCount} properties mapped`);
      })
      .catch(error => {
        console.error('AI mapping retry error:', error);
        const errorMessage = error.message || 'An error occurred during AI mapping retry';
        setAiMappingError(errorMessage);
        setAiMappingComplete(true);
        
        // Check if we have partial results from the retry
        const hasPartialResults = aiMappingStats && aiMappingStats.matched > 0;
        if (!hasPartialResults) {
          console.error(`AI mapping retry failed: ${errorMessage}`);
        }
      })
      .finally(() => {
        setAiMappingAbortController(null);
      });
  }, [
    inputSchema,
    outputSchema,
    aiProgressDetail,
    applyAISuggestions,
    aiMappingStats
  ]);

  // Handle AI conflict resolution from summary dialog
  const handleAIConflictResolution = useCallback((resolvedConflicts: { [outputPath: string]: string }) => {
    if (!aiMappingSummaryData || pendingAITransforms.length === 0) return;

    // Filter out conflicting transforms and keep only resolved ones
    const finalTransforms = pendingAITransforms.filter(transform => {
      const outputPath = transform.outputPath;
      if (!outputPath) return true; // Keep transforms without output paths
      
      // Check if this transform's output conflicts with others
      const conflict = aiMappingSummaryData.conflicts.find(c => c.outputPath === outputPath);
      if (!conflict) return true; // No conflict, keep it
      
      // Keep only if this transform's input matches the resolved choice
      const chosenInputPath = resolvedConflicts[outputPath];
      return transform.inputPath === chosenInputPath;
    });

    // Add the resolved transforms
    if (finalTransforms.length > 0) {
      onAddTransforms(finalTransforms);
    }

    // Clean up state
    setShowAIMappingSummary(false);
    setAiMappingSummaryData(null);
    setPendingAITransforms([]);
  }, [aiMappingSummaryData, pendingAITransforms, onAddTransforms]);

  // Clean up abort controller on unmount
  useEffect(() => {
    return () => {
      if (aiMappingAbortController) {
        aiMappingAbortController.abort();
      }
    };
  }, [aiMappingAbortController]);

  return {
    // State
    showAIMappingProgressDialog,
    aiProgress,
    aiProgressDetail,
    aiMappingError,
    aiMappingComplete,
    aiMappingStats,
    aiMappingPartialSuccess,
    
    // AI Summary dialog state
    showAIMappingSummary,
    aiMappingSummaryData,
    handleAIConflictResolution,
    setShowAIMappingSummary,
    
    // Actions
    handleAIMatch,
    handleRetryAIMapping,
    setShowAIMappingProgressDialog,
    
    // Abort controller for cancellation
    aiMappingAbortController
  };
};
